import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';

import {
    useAddIndirectSubscriptionMutation,
    useCreateSubscriptionMutation,
    useGetInvestmentFundsQuery,
    useGetShareCategoriesQuery,
    useGetShareSubcategoriesQuery,
} from 'api/main';
import { userSelectors, tunnelSelectors } from 'reducers';
import { currentInvestmentFundSet } from 'reducers/tunnel';
import { useDistributorShareCategories } from 'utils/hooks';
import {
    conventionStatus,
    RoleNames,
    queryStringifyOptions,
    entityKey,
    subscriptionStatus,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelStep } from '../common';
import { LargeChoice, MobileChoice } from './common-components/Choice';
import SubscriptionStartDialog from './common-components/SubscriptionStartDialog';
import { InfoDialog, Loader, ShareCategoryDialog, SubmitButton } from 'components';
import { Grid, Typography } from '@mui/material';
import { getEntityIdFromIRI } from 'utils/helpers/iri';
import { useMatchAtLeastOneRole } from 'utils/hooks/subscriptions';
import { useSmallScreen } from 'utils/hooks/ui';

const PEQAN_PROMOTER_NAME = 'peqan';

const NoFundsAvailable = ({ userIsDistributor }) =>
    userIsDistributor ? (
        <>
            <Typography align="center">{texts.NO_CONVENTIONS_FOR_AVAILABLE_FUNDS}</Typography>
            <Typography align="center">{texts.CHECK_YOUR_CONVENTIONS}</Typography>
        </>
    ) : (
        <Typography align="center">{texts.NO_FUNDS_AVAILABLE}</Typography>
    );

NoFundsAvailable.propTypes = { userIsDistributor: PropTypes.bool };

const FundSelect = props => {
    const { setFundSubmitted, fundSubmitted, isIndirect, hasFinishedSubscription } = props;
    const isMobile = useSmallScreen('sm');
    const location = useLocation();
    const dispatch = useDispatch();
    const params = qs.parse(location.search, queryStringifyOptions);
    const userId = params?.userId;
    const selectedShareCategory = useSelector(userSelectors.getShareCategory);

    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);
    const userIsInvestor = useMatchAtLeastOneRole([
        RoleNames.PP_PROFESSIONNEL,
        RoleNames.PM_PROFESSIONNEL,
    ]);

    const { data: investmentFunds = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
        enabled: 1,
        withPrivate: 1,
    });
    const { data: shareCategories = [], isLoading: categoriesLoader } = useGetShareCategoriesQuery(
        {},
        { skip: !userIsDistributor && !userIsInvestor }
    );
    const { data: shareSubcategories = [] } = useGetShareSubcategoriesQuery(
        {},
        { skip: !userIsInvestor }
    );
    const [
        createSubscription,
        { isLoading: createSubscriptionLoading, isSuccess: createSuccessful, reset },
    ] = useCreateSubscriptionMutation();
    const [addIndirectSubscription, { isLoading: addSubscriptionLoading }] =
        useAddIndirectSubscriptionMutation();

    const { id: fundId } = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const [shareCategoryDialogOpen, setShareCategoryDialogOpen] = useState(false);
    const [subscriptionStartDialogOpen, setSubscriptionStartDialogOpen] = useState(false);
    const [categorySelectionErrorOpen, setCategorySelectionErrorOpen] = useState(false);
    const [distributorCategories] = useDistributorShareCategories(fundId);

    // Resolve a subcategory reference (object or IRI) to a full subcategory object
    // carrying fee fields (managementFee, distributorManagementFee, ...)
    const resolveSubcategory = useCallback(
        ref => {
            if (!ref) return null;
            if (typeof ref === 'object' && ref.distributorManagementFee !== undefined) return ref;
            const id =
                typeof ref === 'object' ? ref.id : +getEntityIdFromIRI(ref, entityKey.SUBCATEGORY);
            return shareSubcategories.find(sc => sc.id === +id) || null;
        },
        [shareSubcategories]
    );

    // A "clean share" share unit is one where the distributor management fee is 0
    const isCleanShare = sc => sc?.distributorManagementFee === 0;

    // Did the investor already complete at least one subscription on a clean share unit?
    const hasCleanShareSubscription = useMemo(
        () =>
            (internalUser?.subscriptions || [])
                .filter(s => s.subscriptionStatus === subscriptionStatus.FINISHED)
                .some(sub =>
                    isCleanShare(
                        resolveSubcategory(sub.investmentCharacteristics?.shareSubcategory)
                    )
                ),
        [internalUser, resolveSubcategory]
    );

    const handleChange = useCallback(
        value => () => {
            dispatch(currentInvestmentFundSet(value));
        },
        [dispatch]
    );

    const handleConfirmShareCategory = useCallback(() => {
        setShareCategoryDialogOpen(false);
        if (isIndirect) {
            setSubscriptionStartDialogOpen(true);
        } else {
            setFundSubmitted(true);
        }
    }, [setFundSubmitted, isIndirect]);

    const handleStartSubscription = useCallback(() => {
        setSubscriptionStartDialogOpen(false);
        if (userId) {
            let fundShareCategories = shareCategories.find(
                cat => getEntityIdFromIRI(cat.investmentFund, entityKey.INVESTMENT_FUND) === +fundId
            );
            // adding a new subscription to an existing subscriber
            addIndirectSubscription({
                userId,
                categoryId: selectedShareCategory?.id || fundShareCategories?.[0]?.id,
            });
        } else {
            // creating an indirect subscription with a new subscriber account - normal tunnel flow
            setFundSubmitted(true);
        }
    }, [
        userId,
        addIndirectSubscription,
        fundId,
        setFundSubmitted,
        shareCategories,
        selectedShareCategory,
    ]);

    const handleDistributorSubmit = useCallback(() => {
        if (distributorCategories.length > 1 || !userId) {
            // distributor either has access to both public and clean shares OR multiple share categories match with the fund
            // the distributor needs to select the category from the dialog OR
            // if only one category matches, it will automatically open the subscription start dialog
            // from the share category dialog confirm callback
            setShareCategoryDialogOpen(true);
            setFundSubmitted();
        } else if (distributorCategories.length === 1 && userId) {
            // adding another subscription to an existing investor when only one share category matches
            addIndirectSubscription({ userId, categoryId: distributorCategories?.[0]?.id });
        } else {
            // this should never be the case, but we leave it here for now
            setFundSubmitted(true);
        }
    }, [addIndirectSubscription, distributorCategories, setFundSubmitted, userId]);

    const handleNewInvestorSubscription = useCallback(() => {
        const fund = investmentFunds.find(f => f.id === fundId);
        const fundShareCategories = shareCategories.filter(
            cat => getEntityIdFromIRI(cat.investmentFund, entityKey.INVESTMENT_FUND) === fund.id
        );
        let selectedCategory;

        if (currentSubscription?.distributorCompany) {
            // investor has a distributor
            selectedCategory = fundShareCategories.find(cat =>
                cat.private
                    ? currentSubscription?.distributorCompany?.privateCategoryCommercialization
                    : currentSubscription?.distributorCompany?.publicCategoryCommercialization
            );
        } else {
            // direct investor (no distributor) non-initial subscription
            // all share categories are eligible (private and non-private)
            let candidateCategories;
            if (hasCleanShareSubscription) {
                // investor already holds a clean share unit → keep categories that hold at
                // least one non-reserved clean share subcategory (distributorManagementFee === 0)
                candidateCategories = fundShareCategories.filter(cat =>
                    (cat.shareSubcategories || [])
                        .map(resolveSubcategory)
                        .some(sc => sc && isCleanShare(sc) && !sc.reserved)
                );
            } else {
                // otherwise → among non-clean-share, non-reserved subcategories, find the
                // ones tied at the highest AIFM management fee (managementFee, not distributor
                // fee); keep the unique categories that hold them
                const eligibleSubsWithCategory = fundShareCategories.flatMap(cat =>
                    (cat.shareSubcategories || [])
                        .map(resolveSubcategory)
                        .filter(sc => sc && !isCleanShare(sc) && !sc.reserved)
                        .map(sc => ({ category: cat, managementFee: sc.managementFee ?? 0 }))
                );
                if (eligibleSubsWithCategory.length === 0) {
                    candidateCategories = [];
                } else {
                    const maxFee = Math.max(...eligibleSubsWithCategory.map(e => e.managementFee));
                    // dedupe by category id (a single category can hold several top
                    // subcategories — still counts as one candidate)
                    candidateCategories = Array.from(
                        new Map(
                            eligibleSubsWithCategory
                                .filter(e => e.managementFee === maxFee)
                                .map(e => [e.category.id, e.category])
                        ).values()
                    );
                }
            }
            if (candidateCategories.length !== 1) {
                // 0 or >1 matches → ambiguous selection, ask the user to contact the
                // management company
                setCategorySelectionErrorOpen(true);
                return;
            }
            selectedCategory = candidateCategories[0];
        }
        createSubscription({ shareCategoryId: selectedCategory?.id });
    }, [
        investmentFunds,
        createSubscription,
        currentSubscription?.distributorCompany,
        hasCleanShareSubscription,
        resolveSubcategory,
        shareCategories,
        fundId,
    ]);

    const handleSubmit = useCallback(() => {
        if (userIsDistributor) {
            handleDistributorSubmit();
        } else if (hasFinishedSubscription && userIsInvestor) {
            handleNewInvestorSubscription();
        } else {
            // first direct subscription flow
            setFundSubmitted(true);
        }
    }, [
        hasFinishedSubscription,
        setFundSubmitted,
        userIsDistributor,
        userIsInvestor,
        handleDistributorSubmit,
        handleNewInvestorSubscription,
    ]);

    const filteredFundList = useMemo(
        () =>
            investmentFunds.filter(f => {
                if (userIsDistributor) {
                    // distributors can only use funds that they have fully signed conventions on
                    return currentSubscription?.distributorCompany?.conventions.some(
                        c =>
                            +c.investmentFund?.id === f.id &&
                            c.status === conventionStatus.FULLY_SIGNED
                    );
                }
                // direct subscriptions can only use public funds created by PEQAN
                return !f.private && f.promoter?.name?.toLowerCase() === PEQAN_PROMOTER_NAME;
            }),
        [investmentFunds, currentSubscription, userIsDistributor]
    );

    useEffect(() => {
        if (createSuccessful) {
            reset();
            setFundSubmitted(true);
        }
    }, [createSuccessful, setFundSubmitted, reset]);

    if (fundSubmitted) return null;

    return (
        <TunnelStep
            height="100%"
            overflow="auto"
            maxWidth={1010}
            title={texts.BIENVENUE}
            subtitle={' '}
        >
            <Loader loading={fundsLoader || categoriesLoader}>
                {filteredFundList?.length ? (
                    <Grid
                        container
                        justifyContent="center"
                        sx={{ maxWidth: '1010px', width: '100%', margin: '0 auto 30px' }}
                        gap={4}
                    >
                        {filteredFundList.map(fund =>
                            isMobile ? (
                                <MobileChoice
                                    key={fund.id}
                                    label={`${fund.type} ${fund.name}`}
                                    handleChange={handleChange(fund)}
                                    selected={fundId === fund.id}
                                    selectType="fund"
                                    backgroundImage={fund.image.url}
                                />
                            ) : (
                                <LargeChoice
                                    key={fund.id}
                                    label={`${fund.type} ${fund.name}`}
                                    handleChange={handleChange(fund)}
                                    selected={fundId === fund.id}
                                    selectType="fund"
                                    backgroundImage={fund.image.url}
                                />
                            )
                        )}
                    </Grid>
                ) : (
                    <NoFundsAvailable userIsDistributor={userIsDistributor} />
                )}
            </Loader>
            <SubmitButton
                text={texts.SUIVANT}
                disabled={!fundId}
                loading={addSubscriptionLoading || createSubscriptionLoading}
                onClick={handleSubmit}
            />
            <ShareCategoryDialog
                open={shareCategoryDialogOpen}
                onClose={() => setShareCategoryDialogOpen(false)}
                onConfirm={handleConfirmShareCategory}
            />
            <SubscriptionStartDialog
                open={subscriptionStartDialogOpen}
                handleConfirm={handleStartSubscription}
            />
            <InfoDialog
                open={categorySelectionErrorOpen}
                title={texts.CATEGORY_SELECTION_ERROR_TITLE}
                message={texts.CATEGORY_SELECTION_ERROR}
                handleClose={() => setCategorySelectionErrorOpen(false)}
                withCloseButton
                handleCloseButton={() => setCategorySelectionErrorOpen(false)}
            />
        </TunnelStep>
    );
};

FundSelect.propTypes = {
    setFundSubmitted: PropTypes.func,
    fundSubmitted: PropTypes.bool,
    isIndirect: PropTypes.bool,
    hasFinishedSubscription: PropTypes.bool,
};

export default FundSelect;
